import { useCallback, useEffect, useRef, useState } from "react";
import { useAudioRecorder } from "../../../hooks/useAudioRecorder";
const DEFAULT_MAX_DURATION_SECONDS = 180;
/**
 * Coordinates recording and transcription for a chat composer's voice input.
 *
 * `transcribe` is injected because the endpoint differs per host — the main
 * frontend posts through its authenticated API client, an app through the SDK's
 * — while the state machine around it (auto-stop, discard-on-cancel, one stop
 * per recording) is the same everywhere.
 */ export function useVoiceInput({ transcribe, onTranscription, onError, maxDurationSeconds = DEFAULT_MAX_DURATION_SECONDS }) {
    const { state: recorderState, startRecording: startRec, stopRecording } = useAudioRecorder();
    const [isTranscribing, setIsTranscribing] = useState(false);
    const [elapsed, setElapsed] = useState(0);
    const isRecording = recorderState === "recording";
    // Callers pass fresh closures every render; reading them through refs keeps
    // the returned handlers stable for the timer below and for memoized children.
    const transcribeRef = useRef(transcribe);
    transcribeRef.current = transcribe;
    const onTranscriptionRef = useRef(onTranscription);
    onTranscriptionRef.current = onTranscription;
    const onErrorRef = useRef(onError);
    onErrorRef.current = onError;
    // Prevents double-triggering when the auto-stop timer fires during a manual stop
    const isStoppingRef = useRef(false);
    const startRecording = useCallback(async ()=>{
        const outcome = await startRec();
        if (outcome.status === "failed") {
            onErrorRef.current(outcome.error);
            return;
        }
        // An aborted start left a running recording alone, so resetting the timer
        // here would make it read a duration it never had.
        if (outcome.status === "aborted") return;
        setElapsed(0);
    }, [
        startRec
    ]);
    const stopAndTranscribe = useCallback(async ()=>{
        if (isStoppingRef.current) return;
        isStoppingRef.current = true;
        try {
            const outcome = await stopRecording();
            if (outcome.status === "failed") {
                onErrorRef.current(outcome.error);
                return;
            }
            if (outcome.status === "nothing-recorded" || outcome.blob.size === 0) {
                return;
            }
            setIsTranscribing(true);
            try {
                const text = (await transcribeRef.current(outcome.blob)).trim();
                if (text) onTranscriptionRef.current(text);
            } catch  {
                onErrorRef.current("transcribe-failed");
            } finally{
                setIsTranscribing(false);
            }
        } finally{
            isStoppingRef.current = false;
        }
    }, [
        stopRecording
    ]);
    const cancelRecording = useCallback(async ()=>{
        // The outcome is deliberately ignored: the user asked for the audio to be
        // thrown away, so a recorder that failed on the way out changes nothing
        // they need to hear about. Stopping still releases the microphone.
        await stopRecording();
    }, [
        stopRecording
    ]);
    // Stable ref so the interval always calls the latest stop logic
    const stopAndTranscribeRef = useRef(stopAndTranscribe);
    stopAndTranscribeRef.current = stopAndTranscribe;
    useEffect(function tickElapsedAndAutoStop() {
        if (!isRecording) return;
        let seconds = 0;
        const interval = setInterval(()=>{
            seconds += 1;
            setElapsed(seconds);
            if (seconds >= maxDurationSeconds) {
                // The recorder takes several ticks to settle, and `isRecording` stays
                // true for all of them: without stopping here the timer keeps counting
                // past the maximum and re-enters the stop on every one of them.
                clearInterval(interval);
                void stopAndTranscribeRef.current();
            }
        }, 1000);
        return ()=>clearInterval(interval);
    }, [
        isRecording,
        maxDurationSeconds
    ]);
    return {
        isRecording,
        isTranscribing,
        isActive: isRecording || isTranscribing,
        elapsed,
        startRecording,
        stopAndTranscribe,
        cancelRecording
    };
}

//# sourceMappingURL=use-voice-input.js.map